import React, { useRef, useState } from 'react';
import { Button, DatePicker, Input, Spin, Table, Tag, Divider } from 'antd';
import { PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { ReportFilter, DetailItem, VendorUnitSummary, UnitGrandTotal } from '../../services/d318Api';
import { useD318Report } from './hooks/useD318Report';

const { RangePicker } = DatePicker;

const D318Report: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null);
  const { loading, reportData, queried, noData, runQuery } = useD318Report();

  // Filter state
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [vendorStart, setVendorStart] = useState('');
  const [vendorEnd, setVendorEnd] = useState('');
  const [productStart, setProductStart] = useState('');
  const [productEnd, setProductEnd] = useState('');
  const [deptStart, setDeptStart] = useState('');
  const [deptEnd, setDeptEnd] = useState('');

  const handleQuery = () => {
    const filter: ReportFilter = {
      date_start: dateRange?.[0]?.format('YYYY-MM-DD'),
      date_end: dateRange?.[1]?.format('YYYY-MM-DD'),
      vendor_start: vendorStart || undefined,
      vendor_end: vendorEnd || undefined,
      product_start: productStart || undefined,
      product_end: productEnd || undefined,
      dept_start: deptStart || undefined,
      dept_end: deptEnd || undefined,
    };
    runQuery(filter);
  };

  const handlePrint = () => {
    if (!printRef.current || !reportData) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const now = new Date().toLocaleString('zh-TW');
    win.document.write(`<!DOCTYPE html><html><head><title>繳庫統計表</title>
<style>
  body { font-family: 'Microsoft JhengHei', sans-serif; margin: 20px; font-size: 12px; }
  h2 { text-align: center; margin-bottom: 4px; }
  .meta { text-align: center; font-size: 11px; color: #555; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 3px 6px; }
  th { background: #f0f0f0; text-align: center; }
  .right { text-align: right; }
  .center { text-align: center; }
  .section-title { font-weight: bold; background: #e8e8e8; }
  @media print { body { margin: 10mm; } }
</style></head><body>
<h2>繳庫統計表</h2>
<div class="meta">列印時間: ${now}　　共 ${reportData.total_doc_count} 張單據 / ${reportData.total_item_count} 品項</div>
<table>
<thead>
  <tr class="section-title">
    <th colspan="9">明細資料</th>
  </tr>
  <tr>
    <th>廠商代號</th><th>廠商名稱</th><th>品號</th><th>色號</th><th>品名</th>
    <th>單位</th><th style="text-align:right">數量</th><th style="text-align:right">單價</th><th style="text-align:right">金額</th>
  </tr>
</thead>
<tbody>
${reportData.details.map(r => `<tr>
  <td class="center">${r.vendor_code}</td>
  <td>${r.vendor_name}</td>
  <td class="center">${r.prod_code}</td>
  <td class="center">${r.color_code}</td>
  <td>${r.prod_name}</td>
  <td class="center">${r.unit}</td>
  <td class="right">${r.qty.toFixed(2)}</td>
  <td class="right">${r.unit_price.toFixed(2)}</td>
  <td class="right">${r.amount.toFixed(2)}</td>
</tr>`).join('')}
</tbody>
</table>
<br/>
<table>
<thead>
  <tr class="section-title">
    <th colspan="6">廠商合計（依廠商+單位）</th>
  </tr>
  <tr>
    <th>廠商代號</th><th>廠商名稱</th><th>單位</th>
    <th style="text-align:right">總數量</th><th style="text-align:right">平均單價</th><th style="text-align:right">總金額</th>
  </tr>
</thead>
<tbody>
${reportData.vendor_summary.map(r => `<tr>
  <td class="center">${r.vendor_code}</td><td>${r.vendor_name}</td>
  <td class="center">${r.unit}</td>
  <td class="right">${r.total_qty.toFixed(2)}</td>
  <td class="right">${r.avg_price.toFixed(2)}</td>
  <td class="right">${r.total_amt.toFixed(2)}</td>
</tr>`).join('')}
</tbody>
</table>
<br/>
<table>
<thead>
  <tr class="section-title"><th colspan="3">全部合計（依單位）</th></tr>
  <tr><th>單位</th><th style="text-align:right">總數量</th><th style="text-align:right">總金額</th></tr>
</thead>
<tbody>
${reportData.grand_totals.map(r => `<tr>
  <td class="center">${r.unit}</td>
  <td class="right">${r.total_qty.toFixed(2)}</td>
  <td class="right">${r.total_amt.toFixed(2)}</td>
</tr>`).join('')}
</tbody>
</table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  // Table columns - Details
  const detailColumns: ColumnsType<DetailItem> = [
    { title: '廠商代號', dataIndex: 'vendor_code', key: 'vendor_code', align: 'center', width: 80 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true },
    { title: '品號', dataIndex: 'prod_code', key: 'prod_code', align: 'center', width: 70 },
    { title: '色號', dataIndex: 'color_code', key: 'color_code', align: 'center', width: 60 },
    { title: '品名', dataIndex: 'prod_name', key: 'prod_name', ellipsis: true },
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 60 },
    { title: '數量', dataIndex: 'qty', key: 'qty', align: 'right', width: 90, render: (v: number) => v.toFixed(2) },
    { title: '單價', dataIndex: 'unit_price', key: 'unit_price', align: 'right', width: 90, render: (v: number) => v.toFixed(2) },
    { title: '金額', dataIndex: 'amount', key: 'amount', align: 'right', width: 100, render: (v: number) => v.toFixed(2) },
  ];

  // Table columns - Vendor Summary
  const summaryColumns: ColumnsType<VendorUnitSummary> = [
    { title: '廠商代號', dataIndex: 'vendor_code', key: 'vendor_code', align: 'center', width: 80 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', ellipsis: true },
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 60 },
    { title: '總數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right', width: 100, render: (v: number) => v.toFixed(2) },
    { title: '平均單價', dataIndex: 'avg_price', key: 'avg_price', align: 'right', width: 100, render: (v: number) => v.toFixed(2) },
    { title: '總金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right', width: 110, render: (v: number) => v.toFixed(2) },
  ];

  // Table columns - Grand Totals
  const grandTotalColumns: ColumnsType<UnitGrandTotal> = [
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 80 },
    { title: '總數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right', width: 120, render: (v: number) => v.toFixed(2) },
    { title: '總金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right', width: 130, render: (v: number) => v.toFixed(2) },
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-7xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-4">繳庫統計表 (pwd318f1)</h2>

        {/* Filter Panel */}
        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm font-medium w-16">入庫日期</span>
            <RangePicker
              value={dateRange}
              onChange={(v) => setDateRange(v as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
              format="YYYY-MM-DD"
              placeholder={['起始日期', '結束日期']}
              style={{ width: 280 }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm font-medium w-16">廠商代號</span>
            <Input value={vendorStart} onChange={e => setVendorStart(e.target.value)} placeholder="起" style={{ width: 100 }} maxLength={6} />
            <span className="text-sm">~</span>
            <Input value={vendorEnd} onChange={e => setVendorEnd(e.target.value)} placeholder="迄" style={{ width: 100 }} maxLength={6} />
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm font-medium w-16">品號+色號</span>
            <Input value={productStart} onChange={e => setProductStart(e.target.value)} placeholder="起 (品號+色號)" style={{ width: 140 }} maxLength={12} />
            <span className="text-sm">~</span>
            <Input value={productEnd} onChange={e => setProductEnd(e.target.value)} placeholder="迄 (品號+色號)" style={{ width: 140 }} maxLength={12} />
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm font-medium w-16">部門代號</span>
            <Input value={deptStart} onChange={e => setDeptStart(e.target.value)} placeholder="起" style={{ width: 100 }} maxLength={4} />
            <span className="text-sm">~</span>
            <Input value={deptEnd} onChange={e => setDeptEnd(e.target.value)} placeholder="迄" style={{ width: 100 }} maxLength={4} />
          </div>
          <div className="flex gap-2">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}>查詢</Button>
            {reportData && (
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印預覽</Button>
            )}
          </div>
        </div>

        {/* Results */}
        {queried && !noData && reportData && (
          <div ref={printRef}>
            {/* Summary stats */}
            <div className="flex gap-4 mb-3">
              <Tag color="blue">單據數: {reportData.total_doc_count}</Tag>
              <Tag color="green">品項數: {reportData.total_item_count}</Tag>
            </div>

            {/* Details table */}
            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>明細資料</Divider>
            <Table<DetailItem>
              dataSource={reportData.details}
              columns={detailColumns}
              rowKey={(r) => `${r.vendor_code}-${r.prod_code}-${r.color_code}`}
              size="small"
              pagination={false}
              scroll={{ x: 800 }}
              bordered
              className="mb-4"
            />

            {/* Vendor+Unit summary table */}
            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>廠商合計（依廠商+單位）</Divider>
            <Table<VendorUnitSummary>
              dataSource={reportData.vendor_summary}
              columns={summaryColumns}
              rowKey={(r) => `${r.vendor_code}-${r.unit}`}
              size="small"
              pagination={false}
              scroll={{ x: 600 }}
              bordered
              className="mb-4"
            />

            {/* Grand totals table */}
            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>全部合計（依單位）</Divider>
            <Table<UnitGrandTotal>
              dataSource={reportData.grand_totals}
              columns={grandTotalColumns}
              rowKey={(r) => r.unit}
              size="small"
              pagination={false}
              scroll={{ x: 400 }}
              bordered
            />
          </div>
        )}

        {queried && noData && (
          <div className="text-center text-gray-400 py-8">查無符合條件的繳庫資料!</div>
        )}
      </div>
    </Spin>
  );
};

export default D318Report;
